import cn from "classnames";
import Styles from "./LineScore.module.scss";
import { WinnerArrow } from "src/components/atoms/WinnerArrow";
import { SaberLinescore } from "src/utils/sabertooth-client";
import { teamAbbrev } from "src/components/pages/Events/game-stream-helpers";

export interface LineScoreProps {
  isTie: boolean;
  home: any;
  away: any;
  // Per-inning runs + R/H/E totals from the sabertooth snapshot, keyed by GC team id.
  linescore?: SaberLinescore;
  homeTeamId?: string;
  awayTeamId?: string;
  // Live state pads out future innings (blank, not "X") and hides the winner arrows.
  isLive?: boolean;
  inningsPerGame?: number;
  currentInning?: number;
}

export const LineScore = ({
  isTie,
  home,
  away,
  linescore,
  homeTeamId,
  awayTeamId,
  isLive,
  inningsPerGame,
  currentInning,
}: LineScoreProps) => {
  const awayLine = awayTeamId ? linescore?.[awayTeamId] : undefined;
  const homeLine = homeTeamId ? linescore?.[homeTeamId] : undefined;
  const awayRuns = awayLine?.runsPerInning ?? [];
  const homeRuns = homeLine?.runsPerInning ?? [];
  const batted = Math.max(awayRuns.length, homeRuns.length);

  // Live: pad to regulation length (and the current inning) so upcoming innings show as blanks.
  // Final: show exactly the innings played (falling back to a blank regulation grid while loading).
  const numInnings = isLive
    ? Math.max(inningsPerGame ?? 7, currentInning ?? 0, batted, 1)
    : batted > 0
    ? batted
    : inningsPerGame ?? 7;
  const innings = Array.from({ length: numInnings }, (_, i) => i + 1);

  // A run count for a half-inning already batted; blank for innings still to come (live) or while
  // loading; "X" only for a finished game where the team never batted that half (e.g. home ahead).
  const inningCell = (runs: number[], i: number) => {
    if (i < runs.length) return runs[i];
    if (isLive || batted === 0) return "";
    return "X";
  };
  const total = (line: typeof awayLine, key: "totalRuns" | "totalHits" | "totalErrors", fallback?: number) =>
    line ? line[key] : fallback ?? "";

  // Arrows mark the winner on finished games only; live games show none.
  const showAwayArrow = !isLive && !away.lost && !isTie;
  const showHomeArrow = !isLive && !home.lost && !isTie;

  return (
    <div className={cn(Styles.lineScoreContainer, Styles.dark)}>
      <table className={cn(Styles.lineScore)}>
        <thead>
          <tr data-testid="team-header">
            <th className={cn(Styles.stickyTeamCol)}>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          <tr data-testid="away-row">
            <td className={cn(Styles.stickyTeamCol)}>
              <div>
                <WinnerArrow points="right" className={Styles.arrowCell} show={showAwayArrow} />
                <WinnerArrow points="right" className={Styles.arrowCell} show={showAwayArrow} />
                <span className={cn(Styles.teamAbbr, away.lost && Styles.lost)}>{teamAbbrev(away.name)}</span>
              </div>
            </td>
          </tr>
          <tr data-testid="home-row">
            <td className={cn(Styles.stickyTeamCol)}>
              <div>
                <WinnerArrow points="right" className={Styles.arrowCell} show={showHomeArrow} />
                <WinnerArrow points="right" className={Styles.arrowCell} show={showHomeArrow} />
                <span className={cn(Styles.teamAbbr, home.lost && Styles.lost)}>{teamAbbrev(home.name)}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div className={cn(Styles.scores)}>
        <table className={cn(Styles.lineScore)}>
          <thead>
            <tr data-testid="inning-header">
              {innings.map((n) => (
                <th key={n}>{n}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr data-testid="away-row-innings">
              {innings.map((_, i) => (
                <td key={i}>{inningCell(awayRuns, i)}</td>
              ))}
            </tr>
            <tr data-testid="home-row-innings">
              {innings.map((_, i) => (
                <td key={i}>{inningCell(homeRuns, i)}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <table className={cn(Styles.lineScore, Styles.stickyRHECol)}>
        <thead>
          <tr>
            <th>R</th>
            <th>H</th>
            <th>E</th>
          </tr>
        </thead>
        <tbody className={cn(Styles.runsHitsErrors)}>
          <tr data-testid="away-row-rhe">
            <td>{total(awayLine, "totalRuns", away.score)}</td>
            <td>{total(awayLine, "totalHits")}</td>
            <td>{total(awayLine, "totalErrors")}</td>
          </tr>
          <tr data-testid="home-row-rhe">
            <td>{total(homeLine, "totalRuns", home.score)}</td>
            <td>{total(homeLine, "totalHits")}</td>
            <td>{total(homeLine, "totalErrors")}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
